import React, {useState, useEffect, useRef} from 'react'
import {MapContainer, TileLayer, Marker, Popup, useMap} from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {toast} from 'react-toastify'

// Fix Leaflet marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

type Props = {
  cep: string
  latitude?: string
  longitude?: string
  onLocationConfirm: (lat: string, lon: string) => void
}

interface ViaCepResponse {
  cep: string
  logradouro: string
  complemento: string
  bairro: string
  localidade: string
  uf: string
  ibge: string
  gia: string
  ddd: string
  siafi: string
}

// Move o foco do mapa quando o centro/zoom mudam.
// Necessário porque, no react-leaflet v4, a prop "center" do MapContainer
// só é aplicada na montagem — mudanças posteriores não movem a visualização.
const MapFocusController: React.FC<{center: [number, number]; zoom: number}> = ({center, zoom}) => {
  const map = useMap()

  useEffect(() => {
    map.flyTo(center, zoom, {duration: 1.2})
  }, [center, zoom, map])

  return null
}

const MarkerComponent: React.FC<{
  initialLat: number
  initialLon: number
  onMarkerMove: (lat: number, lon: number) => void
}> = ({initialLat, initialLon, onMarkerMove}) => {
  const [position, setPosition] = useState<[number, number]>([initialLat, initialLon])
  const markerRef = useRef<L.Marker>(null)

  useEffect(() => {
    setPosition([initialLat, initialLon])
  }, [initialLat, initialLon])

  return position === null ? null : (
    <Marker
      position={position}
      ref={markerRef}
      draggable={true}
      eventHandlers={{
        dragend() {
          const marker = markerRef.current
          if (marker != null) {
            const newPos = marker.getLatLng()
            setPosition([newPos.lat, newPos.lng])
            onMarkerMove(newPos.lat, newPos.lng)
            toast.info('Localização atualizada')
          }
        },
      }}
    >
      <Popup>
        Arraste o marcador para ajustar
        <br />
        Lat: {position[0].toFixed(6)}
        <br />
        Lon: {position[1].toFixed(6)}
      </Popup>
    </Marker>
  )
}

const TemploMapComponent: React.FC<Props> = ({cep, latitude, longitude, onLocationConfirm}) => {
  const [lat, setLat] = useState<number>(parseFloat(latitude || '-15.7942'))
  const [lon, setLon] = useState<number>(parseFloat(longitude || '-47.8822'))
  const [loading, setLoading] = useState(false)
  const [centerLat, setCenterLat] = useState<number>(lat)
  const [centerLon, setCenterLon] = useState<number>(lon)
  const [mapZoom, setMapZoom] = useState<number>(13)

  // Buscar coordenadas via ViaCEP
  useEffect(() => {
    if (cep && cep.replace(/\D/g, '').length === 8) {
      setLoading(true)
      const cleanCep = cep.replace(/\D/g, '')

      fetch(`https://viacep.com.br/ws/${cleanCep}/json/`)
        .then((res) => res.json())
        .then((data: ViaCepResponse & {erro?: boolean}) => {
          if (data.erro) {
            toast.error('CEP não encontrado')
            setLoading(false)
            return
          }

          // Usar Google Geocoding para obter lat/lon a partir do endereço ViaCEP
          const address = `${data.logradouro}, ${data.localidade}, ${data.uf}, Brasil`

          // Como alternativa sem API key, usar coordenadas aproximadas de cidades brasileiras
          // Para um caso real, seria melhor usar Google Maps Geocoding ou Nominatim
          geocodeAddress(address, data.localidade, data.uf)
        })
        .catch((err) => {
          toast.error('Erro ao buscar CEP')
          console.error(err)
          setLoading(false)
        })
    }
  }, [cep])

  const geocodeAddress = async (address: string, cidade: string, estado: string) => {
    try {
      // Usando Nominatim (OpenStreetMap) que não requer API key
      const encodedAddress = encodeURIComponent(`${address}`)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}`
      )
      const results = await response.json()

      if (results && results.length > 0) {
        const newLat = parseFloat(results[0].lat)
        const newLon = parseFloat(results[0].lon)
        setLat(newLat)
        setLon(newLon)
        setCenterLat(newLat)
        setCenterLon(newLon)
        // Aproxima o zoom para nível de rua ao focar a localização encontrada
        setMapZoom(16)
        toast.success(`Localização encontrada: ${cidade}, ${estado}`)
      } else {
        toast.warning('Coordenadas não encontradas. Ajuste manualmente no mapa.')
      }
    } catch (err) {
      console.error('Erro ao geocodificar:', err)
      toast.warning('Não foi possível geocodificar automaticamente')
    } finally {
      setLoading(false)
    }
  }

  const handleMarkerMove = (newLat: number, newLon: number) => {
    setLat(newLat)
    setLon(newLon)
  }

  const handleConfirmLocation = () => {
    onLocationConfirm(lat.toString(), lon.toString())
    toast.success('Localização confirmada!')
  }

  return (
    <div className='mb-5'>
      <label className='form-label fw-bold fs-6'>Localização no Mapa</label>

      {loading && (
        <div className='alert alert-info mb-3'>
          <span className='spinner-border spinner-border-sm me-2'></span>
          Buscando localização...
        </div>
      )}

      <div
        style={{
          border: '1px solid #ddd',
          borderRadius: '0.475rem',
          overflow: 'hidden',
          marginBottom: '10px',
        }}
      >
        <MapContainer
          center={[centerLat, centerLon]}
          zoom={13}
          style={{height: '400px', width: '100%'}}
        >
          <MapFocusController center={[centerLat, centerLon]} zoom={mapZoom} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          />
          <MarkerComponent initialLat={lat} initialLon={lon} onMarkerMove={handleMarkerMove} />
        </MapContainer>
      </div>

      <div className='alert alert-light' style={{fontSize: '0.875rem'}}>
        <strong>Instruções:</strong>
        <ul className='mb-0 mt-2'>
          <li>Arraste o marcador para ajustar a localização exata</li>
          <li>
            Latitude: <strong>{lat.toFixed(6)}</strong>
          </li>
          <li>
            Longitude: <strong>{lon.toFixed(6)}</strong>
          </li>
        </ul>
      </div>

      <button type='button' className='btn btn-primary btn-sm' onClick={handleConfirmLocation}>
        Confirmar Localização
      </button>
    </div>
  )
}

export {TemploMapComponent}
